import { Component, OnInit, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ApiClientService } from '../../core/api/api-client.service';
import {
  CertificateTemplateResponse,
  UpdateCertificateTemplateRequest
} from '../../core/models/portal.models';

interface TemplateFormState {
  name: string;
  description: string;
  templatePdfFileName: string;
  templatePdfBase64: string | null;
  resetTemplatePdf: boolean;
}

const EMPTY_FORM: TemplateFormState = {
  name: '',
  description: '',
  templatePdfFileName: '',
  templatePdfBase64: null,
  resetTemplatePdf: false
};

@Component({
  selector: 'app-certificate-template-page',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './certificate-template-page.component.html'
})
export class CertificateTemplatePageComponent implements OnInit {
  private readonly api = inject(ApiClientService);

  protected readonly template = signal<CertificateTemplateResponse | null>(null);
  protected readonly form = signal<TemplateFormState>({ ...EMPTY_FORM });
  protected readonly saving = signal(false);
  protected readonly downloading = signal(false);
  protected readonly notice = signal<string | null>(null);

  ngOnInit(): void {
    this.loadTemplate();
  }

  protected updateForm(field: keyof Pick<TemplateFormState, 'name' | 'description'>, event: Event): void {
    const value = (event.target as HTMLInputElement | HTMLTextAreaElement).value;
    this.form.update((current) => ({ ...current, [field]: value }));
  }

  protected async selectTemplateFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    try {
      const templatePdfBase64 = await this.readFileAsBase64(file);
      this.form.update((current) => ({
        ...current,
        templatePdfFileName: file.name,
        templatePdfBase64,
        resetTemplatePdf: false
      }));
      this.notice.set(`Neue Datei ausgewählt: ${file.name}`);
    } catch {
      this.notice.set('Die ausgewählte Datei konnte nicht gelesen werden.');
    }
  }

  protected resetToDefault(): void {
    this.form.update((current) => ({
      ...current,
      templatePdfFileName: '',
      templatePdfBase64: null,
      resetTemplatePdf: true
    }));
    this.notice.set('Die Standardvorlage wird beim Speichern wieder aktiviert.');
  }

  protected save(event: Event): void {
    event.preventDefault();

    const current = this.form();
    if (!current.name.trim()) {
      this.notice.set('Bitte einen Vorlagennamen angeben.');
      return;
    }

    const request: UpdateCertificateTemplateRequest = {
      name: current.name.trim(),
      description: current.description.trim() || null,
      templatePdfFileName: current.templatePdfFileName || null,
      templatePdfBase64: current.templatePdfBase64,
      resetTemplatePdf: current.resetTemplatePdf
    };

    this.saving.set(true);
    this.notice.set(null);

    this.api.updateCertificateTemplate(request).subscribe({
      next: (template) => {
        this.template.set(template);
        this.form.set({
          name: template.name,
          description: template.description ?? '',
          templatePdfFileName: '',
          templatePdfBase64: null,
          resetTemplatePdf: false
        });
        this.saving.set(false);
        this.notice.set('Die Zertifikatsvorlage wurde gespeichert.');
      },
      error: () => {
        this.saving.set(false);
        this.notice.set('Die Zertifikatsvorlage konnte nicht gespeichert werden.');
      }
    });
  }

  protected downloadTemplate(): void {
    if (this.downloading()) {
      return;
    }

    this.downloading.set(true);
    const fileName = this.template()?.templatePdfFileName ?? 'certificate-template.pdf';
    this.api.downloadCertificateTemplate().subscribe({
      next: (blob) => {
        this.saveBlob(blob, fileName);
        this.downloading.set(false);
      },
      error: () => {
        this.notice.set('Die Vorlage konnte nicht geladen werden.');
        this.downloading.set(false);
      }
    });
  }

  protected formatDateTime(value: string): string {
    return new Intl.DateTimeFormat('de-DE', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
  }

  private loadTemplate(): void {
    this.api.certificateTemplate().subscribe((template) => {
      this.template.set(template);
      this.form.set({
        name: template.name,
        description: template.description ?? '',
        templatePdfFileName: '',
        templatePdfBase64: null,
        resetTemplatePdf: false
      });
    });
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        const commaIndex = result.indexOf(',');
        resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private saveBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.rel = 'noopener';
    anchor.click();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1500);
  }
}
